import { getEnvFallbackWorkspaceId } from '@/lib/env'
import { getAuthItem } from '@/lib/authStorage'

export const SELECTED_CONTAINER_KEY = 'xavior_selected_container'

/**
 * Read the selected container's workspace ID from auth storage.
 * Returns null when no container is selected or the stored value is invalid.
 *
 * Prefers container.id (space ID) over container.ownerId (user ID) —
 * they diverge once team workspaces exist.
 */
export function getSelectedWorkspaceId(): string | null {
  if (typeof window === 'undefined') return null
  const raw = getAuthItem(SELECTED_CONTAINER_KEY)
  if (!raw) return null

  try {
    const parsed = JSON.parse(raw) as { id?: string; ownerId?: string } | null
    const id = typeof parsed?.id === 'string' && parsed.id.trim().length > 0
      ? parsed.id.trim()
      : null
    if (id) return id

    const ownerId = typeof parsed?.ownerId === 'string' && parsed.ownerId.trim().length > 0
      ? parsed.ownerId.trim()
      : null
    if (ownerId) return ownerId
  } catch {
    // Malformed storage — fall through
  }

  return null
}

/**
 * Resolve the active workspace ID.
 * Selected container > env fallback > sentinel.
 *
 * Workspace is determined by container selection (fetched from API
 * after login), never inferred from user identity.
 *
 * During the brief window between login and container fetch (~1s),
 * this returns the env fallback ('default-workspace'). The backend's
 * getWorkspaceIdHeader() filters this sentinel, so workspace-scoped
 * routes return 400 WORKSPACE_CONTEXT_REQUIRED. This window is safe:
 * user-scoped routes (auth, session) don't need workspace context,
 * and workspace-scoped UI doesn't render until containers are loaded.
 */
export function getActiveWorkspaceId(): string {
  const selected = getSelectedWorkspaceId()
  if (selected) return selected

  return getEnvFallbackWorkspaceId()
}

export function wsPath(path: string): string {
  return `/workspaces/${getActiveWorkspaceId()}${path}`
}
